import React from 'react';
import {
	Box,
	Flex,
	HStack,
	VStack,
	Text,
	Avatar,
	Icon,
	IconButton,
	Button,
	Badge,
	Input,
	SimpleGrid,
	Image,
	Divider,
	Drawer,
	DrawerOverlay,
	DrawerContent,
	DrawerBody,
	Spinner,
} from '@chakra-ui/react';
import CustomTooltip from 'components/shared/CustomTooltip';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import {
	FiX,
	FiCopy,
	FiExternalLink,
	FiLink,
	FiBookmark,
	FiArchive,
	FiStar,
	FiCheck,
	FiTag,
	FiFileText,
	FiVideo,
} from 'react-icons/fi';
import useAppToast from 'components/toast';
import { useGetConversationQuery, useLeadSuggestionsQuery, useLinkLeadMutation } from 'api/metaWhatsappApi';
import { formatWaNumber, conversationTitle } from './phone';
import WindowChip from './WindowChip';
import OwnershipBar from './OwnershipBar';
import MediaLightbox from './MediaLightbox';
import { useMediaList } from './mediaRegistry';
import { usePrefs, prefs, LABELS } from '../hooks/useLocalPrefs';

/**
 * WhatsApp's "contact info" pane, for a CRM: who the customer is, which of
 * our numbers they wrote to, who is handling them, the leads they are linked
 * to, and the media the thread has loaded.
 *
 * A column at wide widths, a drawer below — the same component either way,
 * so the two never drift. Pin, archive, labels and starred are per-browser
 * (useLocalPrefs) and say so.
 */
function Section({ title, children }) {
	return (
		<Box px="4" py="3">
			<Text fontSize="xs" fontWeight="semibold" color="wa.inkMuted" textTransform="uppercase" letterSpacing="wide" mb="2">
				{title}
			</Text>
			{children}
		</Box>
	);
}

function LinkLead({ conversationId, phone, contactHidden }) {
	const [q, setQ] = React.useState('');
	const [debounced] = useDebounce(q, 300);
	const { data, isFetching } = useLeadSuggestionsQuery({ q: debounced }, { skip: debounced.trim().length < 2 });
	const [linkLead, { isLoading }] = useLinkLeadMutation();
	const notify = useAppToast();
	const leads = data?.leads ?? [];

	const link = async (lead) => {
		try {
			await linkLead({ conversationId, leadId: lead._id }).unwrap();
			notify.success('Lead linked', lead.leadName || 'Linked to this conversation.');
			setQ('');
		} catch (err) {
			notify.error('Not linked', err?.data?.error || 'This lead could not be linked.');
		}
	};

	return (
		<Box>
			<Input
				size="sm"
				value={q}
				onChange={(e) => setQ(e.target.value)}
				// Without "Lead contact details" the server matches names only, so
				// the box must not invite a number.
				placeholder={contactHidden ? 'Search leads by name' : `Search leads by name or ${phone ? 'number' : 'phone'}`}
				bg="wa.input"
				border="none"
				borderRadius="full"
				color="wa.ink"
				_placeholder={{ color: 'wa.inkMuted' }}
			/>
			{debounced.trim().length >= 2 && (
				<VStack align="stretch" spacing="0" mt="2" borderWidth="1px" borderColor="wa.divider" borderRadius="md" overflow="hidden">
					{isFetching && (
						<Flex p="2" justify="center">
							<Spinner size="xs" color="wa.accent" />
						</Flex>
					)}
					{!isFetching && leads.length === 0 && (
						<Text fontSize="xs" color="wa.inkMuted" p="2">
							No leads match.
						</Text>
					)}
					{leads.map((lead) => (
						<Flex key={lead._id} px="2" py="1.5" align="center" _hover={{ bg: 'wa.hover' }}>
							<Box flex="1" minW="0">
								<Text fontSize="sm" color="wa.ink" noOfLines={1}>
									{lead.leadName || 'Unnamed lead'}
								</Text>
								{lead.leadPhoneNumber && (
									<Text fontSize="xs" color="wa.inkMuted">
										{formatWaNumber(lead.leadPhoneNumber)}
									</Text>
								)}
							</Box>
							<Button size="xs" leftIcon={<Icon as={FiLink} />} onClick={() => link(lead)} isLoading={isLoading} variant="outline">
								Link
							</Button>
						</Flex>
					))}
				</VStack>
			)}
		</Box>
	);
}

function PanelBody({ conversationId, onClose }) {
	const { data: conversation, isLoading } = useGetConversationQuery(conversationId, { skip: !conversationId });
	const navigate = useNavigate();
	const notify = useAppToast();
	const p = usePrefs();
	// Everything this thread has fetched so far — labelled as such below.
	// const media = useMediaList(conversationId);
	// const [lightbox, setLightbox] = React.useState(null);

	if (isLoading || !conversation) {
		return (
			<Flex h="100%" align="center" justify="center">
				<Spinner size="sm" color="wa.inkMuted" />
			</Flex>
		);
	}

	const name = conversationTitle(conversation);
	const phone = formatWaNumber(conversation.customerPhone || conversation.waId);
	const pinned = Boolean(p.pinned[conversationId]);
	const archived = Boolean(p.archived[conversationId]);
	const labels = p.labels[conversationId] ?? [];
	const starredCount = Object.keys(p.starred).length;
	const relatedLeads = conversation.relatedLeads ?? [];

	const copyPhone = () => {
		navigator.clipboard?.writeText(phone).then(
			() => notify.success('Copied', phone),
			() => {},
		);
	};

	// const visual = media.filter((m) => m.type === 'image' || m.type === 'video');
	// const docs = media.filter((m) => m.type === 'document');

	return (
		<Flex direction="column" h="100%" bg="wa.app" color="wa.ink" overflow="hidden">
			<Flex px="3" py="2" align="center" bg="wa.header" borderBottomWidth="1px" borderColor="wa.divider">
				<IconButton aria-label="Close contact info" icon={<Icon as={FiX} boxSize="5" />} size="sm" variant="ghost" borderRadius="full" onClick={onClose} mr="2" />
				<Text fontSize="md" fontWeight="semibold">
					Contact info
				</Text>
			</Flex>

			<Box flex="1" overflowY="auto">
				<VStack spacing="1" py="6" px="4" bg="wa.header" borderBottomWidth="1px" borderColor="wa.divider">
					<Avatar w="96px" h="96px" name={name} bg="wa.chip" color="wa.inkMuted" fontSize="2xl" />
					<Text fontSize="lg" fontWeight="semibold" mt="2" textAlign="center">
						{name}
					</Text>
					<HStack spacing="1">
						<Text fontSize="sm" color="wa.inkMuted">
							{phone}
						</Text>
						{/* ★ A masked number has nothing worth copying. The server sends
						    `contactHidden` with the mask for viewers without "Lead contact
						    details", so this is the same decision the API made — not a
						    second one derived in the browser. */}
						{!conversation.contactHidden && (
							<CustomTooltip label="Copy number" hasArrow>
								<IconButton aria-label="Copy number" icon={<Icon as={FiCopy} />} size="xs" variant="ghost" color="wa.inkMuted" onClick={copyPhone} />
							</CustomTooltip>
						)}
					</HStack>
					{conversation.channel?.label && (
						<Text fontSize="xs" color="wa.inkMuted">
							via {conversation.channel.label}
							{conversation.channel.displayPhoneNumber ? ` · ${formatWaNumber(conversation.channel.displayPhoneNumber)}` : ''}
						</Text>
					)}
					<Box mt="2">
						<WindowChip lastInboundAt={conversation.lastInboundAt} />
					</Box>
				</VStack>

				<Section title="Handling">
					<Box mx="-4">
						<OwnershipBar conversation={conversation} />
					</Box>
				</Section>

				<Divider borderColor="wa.divider" />

				<Section title={`Leads · ${relatedLeads.length}`}>
					<VStack align="stretch" spacing="1" mb="3">
						{relatedLeads.length === 0 && (
							<Text fontSize="sm" color="wa.inkMuted">
								No lead linked to this conversation yet.
							</Text>
						)}
						{relatedLeads.map((lead) => (
							<Flex key={lead._id} align="center" px="2" py="1.5" borderRadius="md" _hover={{ bg: 'wa.hover' }}>
								<Text fontSize="sm" flex="1" noOfLines={1}>
									{lead.leadName || 'Unnamed lead'}
								</Text>
								<CustomTooltip label="Open lead" hasArrow>
									<IconButton
										aria-label="Open lead"
										icon={<Icon as={FiExternalLink} />}
										size="xs"
										variant="ghost"
										color="wa.inkMuted"
										// The lead list's own deep link; `/leadHistory` never existed
										// and landed on the dashboard.
										onClick={() => navigate(`/lead?lead=${lead._id}`)}
									/>
								</CustomTooltip>
							</Flex>
						))}
					</VStack>
					<LinkLead conversationId={conversationId} phone={phone} contactHidden={Boolean(conversation.contactHidden)} />
				</Section>

				<Divider borderColor="wa.divider" />

				{/* <Section title={`Media, docs · ${media.length} loaded`}>
					{visual.length === 0 && docs.length === 0 && (
						<Text fontSize="sm" color="wa.inkMuted">
							Nothing loaded yet. Photos and files appear here as the thread loads them.
						</Text>
					)}
					{visual.length > 0 && (
						<SimpleGrid columns={3} spacing="1.5" mb="3">
							{visual.map((m, i) => (
								<Box
									key={m.wamid}
									as="button"
									type="button"
									aria-label="Open media"
									position="relative"
									pt="100%"
									borderRadius="md"
									overflow="hidden"
									bg="wa.chip"
									onClick={() => setLightbox(i)}
								>
									{m.type === 'image' ? (
										<Image src={m.url} alt="" position="absolute" inset="0" w="100%" h="100%" objectFit="cover" />
									) : (
										<Flex position="absolute" inset="0" align="center" justify="center">
											<Icon as={FiVideo} color="wa.inkMuted" boxSize="6" />
										</Flex>
									)}
								</Box>
							))}
						</SimpleGrid>
					)}
					{docs.map((d) => (
						<HStack key={d.wamid} spacing="2" px="2" py="1.5" borderRadius="md" _hover={{ bg: 'wa.hover' }} as="a" href={d.url} target="_blank" rel="noreferrer">
							<Icon as={FiFileText} color="wa.inkMuted" />
							<Text fontSize="sm" noOfLines={1}>
								{d.filename || 'Document'}
							</Text>
						</HStack>
					))}
				</Section> */}

				<Divider borderColor="wa.divider" />

				<Section title="On this device">
					<VStack align="stretch" spacing="1">
						<Button size="sm" variant="ghost" justifyContent="flex-start" leftIcon={<Icon as={FiStar} />} color="wa.ink" isDisabled>
							{starredCount} starred {starredCount === 1 ? 'message' : 'messages'} in your inbox
						</Button>
						<Button size="sm" variant="ghost" justifyContent="flex-start" leftIcon={<Icon as={FiBookmark} color={pinned ? 'wa.accent' : undefined} />} color="wa.ink" onClick={() => prefs.togglePin(conversationId)}>
							{pinned ? 'Unpin chat' : 'Pin chat'}
						</Button>
						<Button size="sm" variant="ghost" justifyContent="flex-start" leftIcon={<Icon as={FiArchive} color={archived ? 'wa.accent' : undefined} />} color="wa.ink" onClick={() => prefs.toggleArchive(conversationId)}>
							{archived ? 'Unarchive chat' : 'Archive chat'}
						</Button>
						<Text fontSize="xs" color="wa.inkMuted" mt="2" mb="1">
							<Icon as={FiTag} mr="1" />
							Labels
						</Text>
						<Flex align="center" flexWrap="wrap" gap="1.5">
							{LABELS.map((l) => {
								const on = labels.includes(l.id);
								return (
									<Badge
										key={l.id}
										as="button"
										type="button"
										colorScheme={l.scheme}
										variant={on ? 'solid' : 'subtle'}
										fontSize="0.65rem"
										textTransform="none"
										borderRadius="full"
										px="2"
										py="0.5"
										cursor="pointer"
										onClick={() => prefs.toggleLabel(conversationId, l.id)}
									>
										{on && <Icon as={FiCheck} mr="1" />}
										{l.name}
									</Badge>
								);
							})}
						</Flex>
					</VStack>
				</Section>
			</Box>

			{/* {lightbox !== null && visual[lightbox] && (
				<MediaLightbox
					isOpen
					onClose={() => setLightbox(null)}
					items={visual.map((m) => ({ wamid: m.wamid, type: m.type, media: { filename: m.filename, caption: m.caption }, occurredAt: m.occurredAt }))}
					index={lightbox}
					onIndexChange={setLightbox}
					conversationId={conversationId}
				/>
			)} */}
		</Flex>
	);
}

export default function ContactInfoPanel({ conversationId, onClose, variant = 'column' }) {
	if (variant === 'drawer') {
		return (
			<Drawer isOpen onClose={onClose} placement="right" size="sm" blockScrollOnMount={false}>
				<DrawerOverlay />
				<DrawerContent>
					<DrawerBody p="0">
						<PanelBody conversationId={conversationId} onClose={onClose} />
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		);
	}
	return <PanelBody conversationId={conversationId} onClose={onClose} />;
}
